import { API_BASE_URL, MIDDLEWARE_BASE_URL } from "../../../../environment";
import { getAuthHeaders } from "../../../../utils/authUtils";
import {
    ConversationsResponse,
    MessagesResponse,
    SendTextMessageRequest,
    ExternalChatMessage,
    EndUserStatusListResponse,
    ExternalChatEndUser,
    SendNewLeadMessageRequest,
    SendNewLeadMessageResponse,
    AgentStatusListResponse,
    UserAgentStatusListResponse,
    UserAgentStatus,
    ThreadMessagesResponse,
    EngatiTenantBotParams,
} from "./chatTypes";

const sanitizeNextUrl = (url?: string | null): string | null => {
    if (!url) return null;
    try {
        const baseParsed = new URL(API_BASE_URL);
        const parsed = new URL(url, API_BASE_URL);
        if (parsed.protocol === "http:" && (baseParsed.protocol === "https:" || (typeof window !== "undefined" && window.location.protocol === "https:"))) {
            parsed.protocol = "https:";
        }
        parsed.host = baseParsed.host;
        parsed.port = baseParsed.port;
        return parsed.toString();
    } catch {
        return url.replace(/^http:\/\//i, "https://");
    }
};

const tenantBotPromises: Partial<Record<number, Promise<any>>> = {};

export const ChatService = {

    getConversations: async (
        tenantId: number,
        userId: number | undefined,
        filters?: {
            search?: string;
            end_user_statuses?: string;
            tab?: string;
            assigned_user_id?: number;
            current_user_role?: string;
            assigned_team?: string;
            external_chat_bot_channel_account_id?: string;
            assigned_user_full_name?: string;
            contact_id?: number | string;
            external_chat_platform_id?: string | number;
            chat_type_key?: string;
            assigned_team_id?: number;
            created_by?: number;
            show_unread_count?: boolean;
            limit?: number;
            page_size?: number;
            external_chat_tenant_bot_id?: number;
        },
        nextUrl?: string | null
    ): Promise<ConversationsResponse> => {
        try {
            const params = new URLSearchParams();

            if (filters?.search) {
                params.append("search", filters.search);
            }
            if (filters?.end_user_statuses) {
                params.append("end_user_statuses", filters.end_user_statuses);
            }
            if (filters?.assigned_user_id) {
                params.append("assigned_user_id", filters.assigned_user_id.toString());
            }
            if (filters?.current_user_role) {
                params.append("current_user_role", filters.current_user_role);
            }
            if (filters?.created_by) {
                params.append("created_by", filters.created_by.toString());
            }
            if (filters?.assigned_team) {
                params.append("assigned_team", filters.assigned_team);
            }
            if (filters?.external_chat_bot_channel_account_id) {
                params.append("external_chat_bot_channel_account_id", filters.external_chat_bot_channel_account_id);
            }
            if (filters?.assigned_user_full_name) {
                params.append("assigned_user_full_name", filters.assigned_user_full_name);
            }
            if (filters?.contact_id) {
                params.append("contact_id", filters.contact_id.toString());
            }
            if (filters?.external_chat_platform_id) {
                params.append("external_chat_platform_id", filters.external_chat_platform_id.toString());
            }
            if (filters?.chat_type_key) {
                params.append("chat_type_key", filters.chat_type_key);
            }
            if (filters?.assigned_team_id) {
                params.append("assigned_team_id", filters.assigned_team_id.toString());
            }
            if (filters?.show_unread_count !== undefined) {
                params.append("show_unread_count", filters.show_unread_count.toString());
            }
            if (filters?.limit !== undefined) {
                params.append("limit", filters.limit.toString());
            }
            if (filters?.page_size !== undefined) {
                params.append("page_size", filters.page_size.toString());
            }
            if (filters?.external_chat_tenant_bot_id !== undefined && filters?.external_chat_tenant_bot_id !== null) {
                params.append("external_chat_tenant_bot_id", filters.external_chat_tenant_bot_id.toString());
            }

            const sanitizedNextUrl = sanitizeNextUrl(nextUrl);
            const url = sanitizedNextUrl || `${API_BASE_URL}/external-chat-end-user/?${params.toString()}&tenant_id=${tenantId}${filters?.chat_type_key === "INTERNAL" ? `&created_by=${userId}` : ""}`;

            const headers = getAuthHeaders();

            const response = await fetch(url, {
                headers,
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to fetch conversations: ${response.statusText}`);
            }

            const data: ConversationsResponse = await response.json();
            if (data?.next) {
                data.next = sanitizeNextUrl(data.next);
            }

            return data;
        } catch (error) {
            throw error;
        }
    },

    getChannels: async (tenantId: number): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/external-chat-channel/?tenant_id=${tenantId}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch channels: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getExternalChatGroupMembers: async (botChannelAccountId: number, nameOrEmail?: string, page: number = 1): Promise<any> => {
        try {
            const params = new URLSearchParams();
            params.append("external_chat_bot_channel_account_id", botChannelAccountId.toString());
            if (nameOrEmail) {
                params.append("name_or_email", nameOrEmail);
            }
            params.append("page", page.toString());
            const url = `${API_BASE_URL}/external-chat-group-member/?${params.toString()}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch group members: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getExternalChatGroupMembersByEmail: async (searchTerm: string): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/external-chat-group-member/?name_or_email=${searchTerm}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch group members: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getExternalChatGroupMembersByEndUserId: async (endUserId: number, tenantId: number): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/external-chat-group-member/?external_chat_end_user_id=${endUserId}&tenant_id=${tenantId}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch group members: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getMessages: async (
        channelEndUserId: number,
        nextUrl?: string | null,
        externalChatTenantBotId?: number
    ): Promise<MessagesResponse> => {
        try {
            const params = new URLSearchParams();
            params.append("external_chat_end_user_id", channelEndUserId.toString());
            if (externalChatTenantBotId !== undefined && externalChatTenantBotId !== null) {
                params.append("external_chat_tenant_bot_id", externalChatTenantBotId.toString());
            }
            const sanitizedNextUrl = sanitizeNextUrl(nextUrl);
            const url = sanitizedNextUrl || `${API_BASE_URL}/external-chat-message/?${params.toString()}`;

            const response = await fetch(url, {
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch messages: ${response.statusText}`);
            }

            const data: MessagesResponse = await response.json();
            if (data?.next) {
                data.next = sanitizeNextUrl(data.next);
            }
            return data;
        } catch (error) {

            throw error;
        }
    },

    markMessagesAsRead: async (
        channelEndUserId: number,
        tenantId: number
    ): Promise<void> => {
        try {
            const url = `${API_BASE_URL}/external-chat-message-bulk-is-read/`;

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    external_chat_end_user_id: channelEndUserId,
                    tenant_id: tenantId,
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to mark messages as read: ${response.statusText}`);
            }
        } catch (error) {

            throw error;
        }
    },

    markAllBotMessagesAsRead: async (
        tenantId: number,
        botId: number
    ): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/external-chat-message/mark-as-read/`;

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    tenant_id: tenantId,
                    external_chat_tenant_bot_id: botId,
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to mark all messages as read: ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    deleteMessages: async (
        messageIds?: number[],
        endUserId?: number
    ): Promise<void> => {
        try {
            let url = `${API_BASE_URL}/external-chat-message/bulk-delete/`;

            if (endUserId) {
                url += `?external_chat_end_user_id=${endUserId}`;
            } else if (messageIds && messageIds.length > 0) {
                url += `?external_chat_message_id=${messageIds.join(',')}`;
            } else {
                throw new Error("Either messageIds or endUserId must be provided");
            }

            const response = await fetch(url, {
                method: "DELETE",
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error(`Failed to delete messages: ${response.statusText}`);
            }
        } catch (error) {
            throw error;
        }
    },

    sendTextMessage: async (
        userId: string,
        message: string,
        senderId?: number,
        platform?: string,
        endUserId?: number,
        isNewLead: boolean = false,
        parent_message_id?: number,
        root_message_id?: number,
        visibility?: "INTERNAL" | "ALL",
        engatiTenantBot?: EngatiTenantBotParams
    ): Promise<ExternalChatMessage> => {
        try {
            if (platform === "LAZADA" || platform === "TIKTOK") {
                const requestBody: any = {
                    end_user_id: endUserId,
                    message: message,
                    sender_id: senderId,
                    packet_type: "TEXT",
                    is_new_lead: isNewLead,
                    parent_message_id,
                    root_message_id
                };

                const response = await fetch(`${MIDDLEWARE_BASE_URL}/${platform.toLowerCase()}/send-message/`, {
                    method: "POST",
                    headers: {
                        ...getAuthHeaders(),
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    try {
                        const errorJson = JSON.parse(errorText);
                        if (errorJson.error === "UN_AUTHORIZED_BOT") {
                            throw new Error("UN_AUTHORIZED_BOT");
                        }
                    } catch (e) {
                        if (e instanceof Error && e.message === "UN_AUTHORIZED_BOT") throw e;
                    }
                    throw new Error(`Failed to send message: ${response.statusText}`);
                }

                const data = await response.json();
                return data;
            }

            const requestBody: any = {
                externalPacketType: "AGENT_MESSAGE",
                body: {
                    packetType: "TEXT",
                    text: {
                        value: message,
                    },
                    timestamp: new Date().toISOString(),
                    parent_message_id,
                    root_message_id
                },
                userId: userId,
                visibility: visibility || "ALL",
                tenant_id: engatiTenantBot?.tenant_id,
                external_chat_tenant_bot_id: engatiTenantBot?.external_chat_tenant_bot_id,
            };

            if (senderId) {
                requestBody.sender_id = senderId;
            }

            const response = await fetch(`${MIDDLEWARE_BASE_URL}/engati-agent-message/send/`, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const errorJson = JSON.parse(errorText);
                    if (errorJson.error === "UN_AUTHORIZED_BOT") {
                        throw new Error("UN_AUTHORIZED_BOT");
                    }
                } catch (e) {
                    if (e instanceof Error && e.message === "UN_AUTHORIZED_BOT") throw e;
                }
                throw new Error(`Failed to send message: ${response.statusText}`);
            }

            const data = await response.json();
            if (data && data.error === "UN_AUTHORIZED_BOT") {
                throw new Error("UN_AUTHORIZED_BOT");
            }
            return data;
        } catch (error) {

            throw error;
        }
    },

    sendMediaMessage: async (
        userId: string,
        file: File,
        messageType: "IMAGE" | "VIDEO" | "AUDIO" | "PDF" | "STICKER",
        senderId?: number,
        platform?: string,
        endUserId?: number,
        isNewLead: boolean = false,
        stickerText?: string,
        parent_message_id?: number,
        root_message_id?: number,
        engatiTenantBot?: EngatiTenantBotParams
    ): Promise<ExternalChatMessage> => {
        try {
            const formData = new FormData();

            if (platform === "LAZADA" || platform === "TIKTOK") {
                if (endUserId) formData.append("end_user_id", endUserId.toString());
                if (senderId) formData.append("sender_id", senderId.toString());
                formData.append("packet_type", messageType);
                if (file) formData.append("file", file);
                formData.append("is_new_lead", isNewLead.toString());
                if (messageType === "STICKER" && stickerText) {
                    formData.append("sticker_text", stickerText);
                }
                if (parent_message_id) formData.append("parent_message_id", parent_message_id.toString());
                if (root_message_id) formData.append("root_message_id", root_message_id.toString());

                const { "Content-Type": _, ...headers } = getAuthHeaders();

                const response = await fetch(`${MIDDLEWARE_BASE_URL}/${platform.toLowerCase()}/send-message/`, {
                    method: "POST",
                    headers: headers,
                    body: formData,
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    try {
                        const errorJson = JSON.parse(errorText);
                        if (errorJson.error === "UN_AUTHORIZED_BOT") {
                            throw new Error("UN_AUTHORIZED_BOT");
                        }
                    } catch (e) {
                        if (e instanceof Error && e.message === "UN_AUTHORIZED_BOT") throw e;
                    }
                    throw new Error(`Failed to send media message: ${response.statusText}`);
                }

                const data = await response.json();
                return data;
            }

            formData.append("externalPacketType", "AGENT_MESSAGE");
            formData.append(
                "body",
                JSON.stringify({
                    packetType: messageType,
                    timestamp: new Date().toISOString(),
                    parent_message_id,
                    root_message_id
                })
            );
            formData.append("userId", userId);
            formData.append("file", file);
            if (senderId) {
                formData.append("sender_id", senderId.toString());
            }
            if (engatiTenantBot?.tenant_id) {
                formData.append("tenant_id", engatiTenantBot.tenant_id.toString());
            }
            if (engatiTenantBot?.external_chat_tenant_bot_id) {
                formData.append("external_chat_tenant_bot_id", engatiTenantBot.external_chat_tenant_bot_id.toString());
            }

            const { "Content-Type": _, ...headers } = getAuthHeaders();

            const response = await fetch(`${MIDDLEWARE_BASE_URL}/engati-agent-message/send/`, {
                method: "POST",
                headers: headers,
                body: formData,
            });

            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const errorJson = JSON.parse(errorText);
                    if (errorJson.error === "UN_AUTHORIZED_BOT") {
                        throw new Error("UN_AUTHORIZED_BOT");
                    }
                } catch (e) {
                    if (e instanceof Error && e.message === "UN_AUTHORIZED_BOT") throw e;
                }
                throw new Error(`Failed to send media message: ${response.statusText}`);
            }

            const data = await response.json();
            if (data && data.error === "UN_AUTHORIZED_BOT") {
                throw new Error("UN_AUTHORIZED_BOT");
            }
            return data;
        } catch (error) {

            throw error;
        }
    },

    getEndUserStatusOptions: async (
        tenantId: number
    ): Promise<EndUserStatusListResponse> => {
        try {
            const url = `${API_BASE_URL}/external-chat-end-user-status/?tenant_id=${tenantId}`;

            const response = await fetch(url, {
                headers: getAuthHeaders(),
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch end user status options: ${response.statusText}`);
            }

            const data: EndUserStatusListResponse = await response.json();
            return data;
        } catch (error) {
            throw error;
        }
    },

    updateEndUserStatus: async (
        externalChatEndUserId: number,
        statusId: number,
        tenantId: number,
        updatedBy: number
    ): Promise<ExternalChatEndUser> => {
        try {
            const url = `${MIDDLEWARE_BASE_URL}/engati-open-close-chat/`;

            const response = await fetch(url, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    tenant_id: tenantId,
                    end_user_id: externalChatEndUserId,
                    end_user_status_id: statusId,
                    user_id: updatedBy,
                }),
            });

            if (!response.ok) {
                throw new Error(`Failed to update end user status: ${response.statusText}`);
            }

            const data: ExternalChatEndUser = await response.json();
            return data;
        } catch (error) {
            throw error;
        }
    },

    getContacts: async (
        tenantId: number,
        userId?: number | null,
        filters?: {
            contact_full_name?: string;
            primary_email?: string;
            company_name?: string;
            page?: number;
            page_size?: number;
        }
    ): Promise<any> => {
        try {
            const params = new URLSearchParams();
            params.append("tenant_id", tenantId.toString());

            if (filters?.contact_full_name) {
                params.append("contact_full_name", filters.contact_full_name);
            }
            if (filters?.primary_email) {
                params.append("primary_email", filters.primary_email);
            }
            if (filters?.company_name) {
                params.append("company_name", filters.company_name);
            }
            if (filters?.page) {
                params.append("page", filters.page.toString());
            }
            if (filters?.page_size) {
                params.append("page_size", filters.page_size.toString());
            }

            let url = `${API_BASE_URL}/contact/?${params.toString()}&is_active=true`;
            if (userId) {
                url += `&sales_user_id=${userId}`;
            }
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch contacts: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getTenantUsers: async (tenantId: number): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/tenant-user/?tenant_id=${tenantId}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch tenant users: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getTeams: async (tenantId: number): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/team/?tenant_id=${tenantId}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch teams: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getTeamUsers: async (teamId: number): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/team-user/?team_id=${teamId}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch team users: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    updateEndUserAssignments: async (
        externalChatEndUserId: number,
        tenantId: number,
        contactId?: number | null,
        assignedUserId?: number | null,
        assignedTeamIds?: number[]
    ): Promise<ExternalChatEndUser> => {
        try {
            const url = `${API_BASE_URL}/external-chat-end-user/${externalChatEndUserId}/`;
            const body: any = { tenant_id: tenantId };
            if (contactId !== undefined) body.contact_id = contactId;
            if (assignedUserId !== undefined) body.assigned_user_id = assignedUserId;
            if (assignedTeamIds !== undefined) body.assigned_team_ids = assignedTeamIds;

            const response = await fetch(url, {
                method: "PATCH",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify(body),
            });

            if (!response.ok) throw new Error(`Failed to update end user assignments: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    sendNewLeadMessage: async (
        userId: string,
        message: string,
        assignedUserId: number,
        platform?: string,
        endUserId?: number,
        parent_message_id?: number,
        root_message_id?: number,
        engatiTenantBot?: EngatiTenantBotParams
    ): Promise<SendNewLeadMessageResponse> => {
        try {
            if (platform === "LAZADA" || platform === "TIKTOK") {
                const requestBody: any = {
                    end_user_id: endUserId,
                    message: message,
                    sender_id: assignedUserId,
                    packet_type: "TEXT",
                    is_new_lead: true,
                    parent_message_id,
                    root_message_id
                };

                const response = await fetch(`${MIDDLEWARE_BASE_URL}/${platform.toLowerCase()}/send-message/`, {
                    method: "POST",
                    headers: {
                        ...getAuthHeaders(),
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    try {
                        const errorJson = JSON.parse(errorText);
                        if (errorJson.error === "UN_AUTHORIZED_BOT") {
                            throw new Error("UN_AUTHORIZED_BOT");
                        }
                    } catch (e) {
                        if (e instanceof Error && e.message === "UN_AUTHORIZED_BOT") throw e;
                    }
                    throw new Error(`Failed to send new lead message: ${response.statusText}`);
                }

                const data: SendNewLeadMessageResponse = await response.json();
                return data;
            }

            const requestBody: any = {
                externalPacketType: "AGENT_MESSAGE",
                body: {
                    packetType: "TEXT",
                    text: {
                        value: message,
                    },
                    timestamp: new Date().toISOString(),
                    parent_message_id,
                    root_message_id
                },
                userId: userId,
                assigned_user_id: assignedUserId,
                sender_id: assignedUserId,
                tenant_id: engatiTenantBot?.tenant_id,
                external_chat_tenant_bot_id: engatiTenantBot?.external_chat_tenant_bot_id,
            };

            const response = await fetch(`${MIDDLEWARE_BASE_URL}/engati-agent-message-for-new-lead/send/`, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const errorJson = JSON.parse(errorText);
                    if (errorJson.error === "UN_AUTHORIZED_BOT") {
                        throw new Error("UN_AUTHORIZED_BOT");
                    }
                } catch (e) {
                    if (e instanceof Error && e.message === "UN_AUTHORIZED_BOT") throw e;
                }
                throw new Error(`Failed to send new lead message: ${response.statusText}`);
            }

            const data: SendNewLeadMessageResponse = await response.json();
            if ((data as any) && (data as any).error === "UN_AUTHORIZED_BOT") {
                throw new Error("UN_AUTHORIZED_BOT");
            }
            return data;
        } catch (error) {
            throw error;
        }
    },

    sendViaEmail: async (
        email: string,
        senderId: number,
        text?: string,
        file?: File,
        tenantId?: number,
        externalChatTenantBotId?: number
    ): Promise<any> => {
        try {
            const formData = new FormData();
            formData.append("email", email);
            formData.append("sender_id", senderId.toString());
            if (text) formData.append("text", text);
            if (file) formData.append("files", file);
            if (tenantId !== undefined && tenantId !== null) {
                formData.append("tenant_id", tenantId.toString());
            }
            if (externalChatTenantBotId !== undefined && externalChatTenantBotId !== null) {
                formData.append("external_chat_tenant_bot_id", externalChatTenantBotId.toString());
            }

            const { "Content-Type": _, ...headers } = getAuthHeaders();

            const response = await fetch(`${MIDDLEWARE_BASE_URL}/google-chat/send-via-email/`, {
                method: "POST",
                headers: headers,
                body: formData,
            });

            const data = await response.json().catch(() => null);
            if (!response.ok) {
                if (data) return data;
                throw new Error(`Failed to send message: ${response.statusText}`);
            }

            return data;
        } catch (error) {
            throw error;
        }
    },

    addReaction: async (payload: {
        sender_id: number;
        end_user_id: number;
        message_id: number;
        reaction: string;
    }): Promise<any> => {
        try {
            const url = `${MIDDLEWARE_BASE_URL}/google-chat/add-reaction/`;
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`Failed to add reaction: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    removeReaction: async (payload: {
        sender_id: number;
        external_chat_message_id: number;
        external_chat_message_reaction_id: number;
    }): Promise<any> => {
        try {
            const url = `${MIDDLEWARE_BASE_URL}/google-chat/remove-reaction/`;
            const response = await fetch(url, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`Failed to remove reaction: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    createContact: async (payload: {
        first_name: string;
        last_name: string;
        tenant_id: number;
        created_by: number;
        updated_by: number;
        primary_email?: string;
        personal_email?: string;
        primary_phone?: string;
        is_active?: boolean;
    }): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/contact/`, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw new Error(`Failed to create contact: ${response.statusText}`);
            }

            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getChatTemplateCategories: async (tenantId: number): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/chat-template-category/?tenant_id=${tenantId}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch template categories: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    createChatTemplateCategory: async (payload: {
        category_name: string;
        description?: string;
        tenant_id: number;
        chat_template_type_id: number;
        created_by: number;
        updated_by: number;
    }): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/chat-template-category/`, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to create template category: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    deleteChatTemplateCategory: async (categoryId: number): Promise<void> => {
        try {
            const response = await fetch(`${API_BASE_URL}/chat-template-category/${categoryId}/`, {
                method: "DELETE",
                headers: getAuthHeaders(),
            });
            if (!response.ok) throw new Error(`Failed to delete template category: ${response.statusText}`);
        } catch (error) {
            throw error;
        }
    },

    updateChatTemplateCategory: async (categoryId: number, payload: {
        category_name?: string;
        description?: string;
        tenant_id?: number;
        updated_by: number;
    }): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/chat-template-category/${categoryId}/`, {
                method: "PATCH",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to update template category: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getChatTemplateMessages: async (tenantId: number, categoryId?: number): Promise<any> => {
        try {
            let url = `${API_BASE_URL}/chat-template-message/?tenant_id=${tenantId}`;
            if (categoryId) {
                url += `&chat_template_category_id=${categoryId}`;
            }
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch template messages: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    createChatTemplateMessage: async (payload: {
        title: string;
        message: string;
        short_code: string;
        chat_template_category_id: number;
        chat_template_type_id: number;
        tenant_id: number;
        created_by: number;
        updated_by: number;
    }): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/chat-template-message/`, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to create template message: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    deleteChatTemplateMessage: async (messageId: number): Promise<void> => {
        try {
            const response = await fetch(`${API_BASE_URL}/chat-template-message/${messageId}/`, {
                method: "DELETE",
                headers: getAuthHeaders(),
            });
            if (!response.ok) throw new Error(`Failed to delete template message: ${response.statusText}`);
        } catch (error) {
            throw error;
        }
    },

    updateChatTemplateMessage: async (messageId: number, payload: {
        title?: string;
        message?: string;
        short_code?: string;
        tenant_id?: number;
        updated_by: number;
    }): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/chat-template-message/${messageId}/`, {
                method: "PATCH",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to update template message: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getChatTemplatePlaceholders: async (tenantId: number): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/chat-template-placeholder/?tenant_id=${tenantId}&is_active=true`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch template placeholders: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getExternalChatTenantBot: async (tenantId: number): Promise<any> => {
        const cached = tenantBotPromises[tenantId];
        if (cached) {
            return cached;
        }
        const promise = (async () => {
            try {
                const auth = JSON.parse(localStorage.getItem("authResponse") || "{}");
                const userId = auth?.user_id || "";
                const url = `${API_BASE_URL}/external-chat-tenant-bot/?tenant_id=${tenantId}&is_deleted=false&is_active=true${userId ? `&created_by=${userId}` : ""}`;
                const response = await fetch(url, { headers: getAuthHeaders() });
                if (!response.ok) throw new Error(`Failed to fetch tenant bots: ${response.statusText}`);
                return await response.json();
            } finally {
                setTimeout(() => {
                    delete tenantBotPromises[tenantId];
                }, 3000);
            }
        })();
        tenantBotPromises[tenantId] = promise;
        return promise;
    },

    getBotChannelAccounts: async (tenantId: number, chatTypeKey?: string): Promise<any> => {
        try {
            let url = `${API_BASE_URL}/external-chat-bot-channel-account/?tenant_id=${tenantId}&is_active=true`;
            if (chatTypeKey) {
                url += `&chat_type_key=${chatTypeKey}`;
            }
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch bot channel accounts: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getBotChannelAccount: async (botId: number, tenantId: number): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/external-chat-bot-channel-account/?external_chat_tenant_bot_id=${botId}&tenant_id=${tenantId}&is_active=true`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch bot channel account: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    createBotChannelAccount: async (payload: any): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/external-chat-bot-channel-account/`, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to create bot channel account: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    updateBotChannelAccount: async (id: number, payload: any): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/external-chat-bot-channel-account/${id}/`, {
                method: "PATCH",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to update bot channel account: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    deleteBotChannelAccount: async (id: number): Promise<void> => {
        try {
            const response = await fetch(`${API_BASE_URL}/external-chat-bot-channel-account/${id}/`, {
                method: "DELETE",
                headers: getAuthHeaders(),
            });
            if (!response.ok) throw new Error(`Failed to delete bot channel account: ${response.statusText}`);
        } catch (error) {
            throw error;
        }
    },

    getPlatforms: async (tenantId?: number, chatTypeKey?: string): Promise<any> => {
        try {
            let url = `${API_BASE_URL}/external-chat-platform/`;
            const params = new URLSearchParams();
            if (tenantId) params.append("tenant_id", tenantId.toString());
            if (chatTypeKey) params.append("chat_type_key", chatTypeKey);

            const queryString = params.toString();
            if (queryString) {
                url += `?${queryString}`;
            }

            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch platforms: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getAgentStatuses: async (): Promise<AgentStatusListResponse> => {
        try {
            const url = `${API_BASE_URL}/agent-status/`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch agent statuses`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getCurrentUserAgentStatus: async (userId: number): Promise<UserAgentStatusListResponse> => {
        try {
            const url = `${API_BASE_URL}/user-agent-status/?user_id=${userId}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch user agent status`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    createUserAgentStatus: async (payload: any): Promise<UserAgentStatus> => {
        try {
            const url = `${API_BASE_URL}/user-agent-status/`;
            const response = await fetch(url, {
                method: "POST",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to create user agent status`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    updateUserAgentStatus: async (userAgentStatusId: number, payload: any): Promise<UserAgentStatus> => {
        try {
            const url = `${API_BASE_URL}/user-agent-status/${userAgentStatusId}/`;
            const response = await fetch(url, {
                method: "PATCH",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to update user agent status`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    sendGoogleChatTextMessage: async (
        senderId: number,
        endUserId: number,
        text: string,
        parent_message_id?: number,
        root_message_id?: number
    ): Promise<any> => {
        try {
            const endpoint = parent_message_id ? "send-quoted-message/" : "send-message/";
            const response = await fetch(`${MIDDLEWARE_BASE_URL}/google-chat/${endpoint}`, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    sender_id: senderId,
                    end_user_id: endUserId,
                    text,
                    parent_message_id: parent_message_id?.toString(),
                    root_message_id: root_message_id?.toString()
                }),
            });
            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const errorJson = JSON.parse(errorText);
                    if (errorJson.error === "UN_AUTHORIZED_BOT") throw new Error("UN_AUTHORIZED_BOT");
                } catch (e) {
                    if (e instanceof Error && e.message === "UN_AUTHORIZED_BOT") throw e;
                }
                throw new Error(`Failed to send Google Chat message: ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    sendGoogleChatMediaMessage: async (
        senderId: number,
        endUserId: number,
        files: File[],
        text?: string,
        parent_message_id?: number,
        root_message_id?: number
    ): Promise<any> => {
        try {
            const endpoint = parent_message_id ? "send-quoted-message/" : "send-message/";
            const formData = new FormData();
            formData.append("sender_id", senderId.toString());
            formData.append("end_user_id", endUserId.toString());
            if (text && text.trim()) {
                formData.append("text", text.trim());
            }
            for (const file of files) {
                formData.append("files", file);
            }
            if (parent_message_id) formData.append("parent_message_id", parent_message_id.toString());
            if (root_message_id) formData.append("root_message_id", root_message_id.toString());
            const { "Content-Type": _, ...headers } = getAuthHeaders();
            const response = await fetch(`${MIDDLEWARE_BASE_URL}/google-chat/${endpoint}`, {
                method: "POST",
                headers,
                body: formData,
            });
            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const errorJson = JSON.parse(errorText);
                    if (errorJson.error === "UN_AUTHORIZED_BOT") throw new Error("UN_AUTHORIZED_BOT");
                } catch (e) {
                    if (e instanceof Error && e.message === "UN_AUTHORIZED_BOT") throw e;
                }
                throw new Error(`Failed to send Google Chat media: ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    reauthorizePlatform: async (platformKey: string): Promise<any> => {
        try {
            let endpoint = (platformKey || "").toLowerCase();
            if (endpoint === "engati") endpoint = "engati";

            if (!endpoint) throw new Error("Unsupported platform for re-authorization");

            const response = await fetch(`${API_BASE_URL}/${endpoint}/auth/`, {
                method: "POST",
                headers: {
                    ...getAuthHeaders(),
                    "Content-Type": "application/json",
                },
            });
            if (!response.ok) throw new Error(`Failed to reauthorize: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getThreadMessages: async (
        tenantId: number,
        externalChatMessageId: number
    ): Promise<ThreadMessagesResponse> => {
        try {
            const params = new URLSearchParams();
            params.append("tenant_id", tenantId.toString());
            params.append("external_chat_message_id", externalChatMessageId.toString());
            const url = `${API_BASE_URL}/external-chat-message/get-thread-message/?${params.toString()}`;
            const response = await fetch(url, { headers: getAuthHeaders() });
            if (!response.ok) throw new Error(`Failed to fetch thread messages: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    sendThreadMessage: async (payload: {
        sender_id: number;
        end_user_id: number;
        parent_message_id: number;
        root_message_id?: number;
        text: string;
        files?: File[];
    }): Promise<any> => {
        try {
            const { files, ...fields } = payload;
            let requestInit: RequestInit;

            if (files && files.length > 0) {
                const formData = new FormData();
                formData.append("sender_id", fields.sender_id.toString());
                formData.append("end_user_id", fields.end_user_id.toString());
                formData.append("parent_message_id", fields.parent_message_id.toString());
                if (fields.root_message_id) formData.append("root_message_id", fields.root_message_id.toString());
                if (fields.text && fields.text.trim()) formData.append("text", fields.text.trim());
                for (const file of files) {
                    formData.append("files", file);
                }
                const { "Content-Type": _, ...headers } = getAuthHeaders();
                requestInit = { method: "POST", headers, body: formData };
            } else {
                requestInit = {
                    method: "POST",
                    headers: {
                        ...getAuthHeaders(),
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        ...fields,
                        parent_message_id: fields.parent_message_id?.toString(),
                        root_message_id: fields.root_message_id?.toString()
                    }),
                };
            }

            const response = await fetch(`${MIDDLEWARE_BASE_URL}/google-chat/send-thread-message/`, requestInit);
            if (!response.ok) {
                const errorText = await response.text();
                try {
                    const errorJson = JSON.parse(errorText);
                    if (errorJson.error === "UN_AUTHORIZED_BOT") throw new Error("UN_AUTHORIZED_BOT");
                } catch (e) {
                    if (e instanceof Error && e.message === "UN_AUTHORIZED_BOT") throw e;
                }
                throw new Error(`Failed to send thread message: ${response.statusText}`);
            }
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    updateEndUser: async (endUserId: number, payload: { username: string; tenant_id: number }): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/external-chat-end-user/${endUserId}/`;
            const response = await fetch(url, {
                method: "PATCH",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to update end user: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    updateGroupMember: async (memberId: number, payload: { member_name: string; tenant_id: number }): Promise<any> => {
        try {
            const url = `${API_BASE_URL}/external-chat-group-member/${memberId}/`;
            const response = await fetch(url, {
                method: "PATCH",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to update group member: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    markSingleRead: async (senderId: number, externalChatTenantBotId: number, externalChatBotChannelAccountId: number): Promise<any> => {
        try {
            const url = `${MIDDLEWARE_BASE_URL}/google-chat-read-state/mark-single-read/`;
            const response = await fetch(url, {
                method: "POST",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({
                    sender_id: senderId,
                    external_chat_tenant_bot_id: externalChatTenantBotId,
                    external_chat_bot_channel_account_id: externalChatBotChannelAccountId,
                }),
            });
            if (!response.ok) throw new Error(`Failed to mark single read: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    syncReadStatus: async (senderId: number, externalChatTenantBotId: number): Promise<any> => {
        try {
            const url = `${MIDDLEWARE_BASE_URL}/google-chat-read-state/sync-read-status/`;
            const response = await fetch(url, {
                method: "POST",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({
                    sender_id: senderId,
                    external_chat_tenant_bot_id: externalChatTenantBotId,
                }),
            });
            if (!response.ok) throw new Error(`Failed to sync read status: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    syncReadStatusBatch: async (senderId: number, externalChatTenantBotId: number, externalChatBotChannelAccountIds: number[]): Promise<any> => {
        try {
            const url = `${MIDDLEWARE_BASE_URL}/google-chat-read-state/sync-read-status-batch/`;
            const response = await fetch(url, {
                method: "POST",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({
                    sender_id: senderId,
                    external_chat_tenant_bot_id: externalChatTenantBotId,
                    external_chat_bot_channel_account_ids: externalChatBotChannelAccountIds,
                }),
            });
            if (!response.ok) throw new Error(`Failed to sync read status batch: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    getFirstLeadStageId: async (tenantId: number): Promise<number | null> => {
        try {
            const typesResponse = await fetch(`${API_BASE_URL}/lifecycle-type/?tenant_id=${tenantId}`, {
                headers: getAuthHeaders(),
            });
            if (!typesResponse.ok) return null;
            const typesData = await typesResponse.json();
            const types = typesData.results || typesData;
            const leadType = Array.isArray(types) ? types.find((t: any) => t.lifecycle_type_key === "LEAD") : null;
            if (!leadType) return null;

            const stagesResponse = await fetch(`${API_BASE_URL}/batch/lifecycle-type-stage/?lifecycle_type_id=${leadType.lifecycle_type_id}`, {
                headers: getAuthHeaders(),
            });
            if (!stagesResponse.ok) return null;
            const stages = await stagesResponse.json();
            if (!Array.isArray(stages) || stages.length === 0) return null;
            const sorted = [...stages].sort((a: any, b: any) => a.stage_order - b.stage_order);
            return sorted[0].lifecycle_type_stage_id;
        } catch (error) {
            return null;
        }
    },

    createLead: async (payload: {
        title: string;
        tenant_id: number;
        created_by: number;
        updated_by: number;
        primary_contact?: number | null;
        lifecycle_type_stage_id?: number | null;
        priority?: string;
        assigned_user_id?: number | null;
        assigned_team_id?: number | null;
    }): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/lead/`, {
                method: "POST",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!response.ok) throw new Error(`Failed to create lead: ${response.statusText}`);
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    createGroupMember: async (payload: {
        tenant_id: number;
        external_chat_bot_channel_account_id: number | null;
        external_chat_platform_id: number;
        user_id: number;
        user_role: string;
        added_by: number;
        external_chat_end_user_id: number;
        from_portal: boolean;
        member_id: string;
        member_name: string;
        member_email: string;
        member_profile_picture: string;
    }): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/external-chat-group-member/`, {
                method: "POST",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to create group member: ${errorText}`);
            }
            return await response.json();
        } catch (error) {
            throw error;
        }
    },

    deleteGroupMember: async (id: number): Promise<any> => {
        try {
            const response = await fetch(`${API_BASE_URL}/external-chat-group-member/${id}/`, {
                method: "DELETE",
                headers: getAuthHeaders(),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to delete group member: ${errorText}`);
            }
            // DELETE requests might not return JSON, so we handle it gracefully
            if (response.status !== 204) {
                try {
                    return await response.json();
                } catch {
                    return { success: true };
                }
            }
            return { success: true };
        } catch (error) {
            throw error;
        }
    },

    transferChatToAgent: async (
        tenantId: number,
        endUserId: number,
        externalChatTenantBotId: number
    ): Promise<any> => {
        try {
            const response = await fetch(`${MIDDLEWARE_BASE_URL}/engati-bot-flow-trigger/`, {
                method: "POST",
                headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
                body: JSON.stringify({
                    tenant_id: tenantId,
                    end_user_id: endUserId,
                    external_chat_tenant_bot_id: externalChatTenantBotId
                }),
            });
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to transfer chat: ${errorText}`);
            }
            return await response.json();
        } catch (error) {
            throw error;
        }
    },
};
